import { useEffect, useState } from "react";
import { Icon, useStudio } from "./ui";
import { MeetingDialog } from "./Meeting";
import { cleanError, graph, m365Available, mailFromGoogle, openLink, setMailProvider, timeOf, useGraph, type CalEvent, type Chat, type Mail } from "../lib/m365";
import { connectGoogle, googleAvailable } from "../lib/google";
import { devMode } from "../auth/auth";
import { dateLocale, t, T } from "../lib/i18n";
import { listProposals, type Proposal } from "../lib/work";

/** Calendar, inbox, Teams and proposal deadlines for the signed-in person: Outlook or Gmail and Google Calendar, in their language. */

/** Connects right here: Gmail or Outlook with one press, no detour through Connections. EduTask sees Gmail first. */
function ConnectHint({ teams }: { teams?: boolean }) {
  const { go, lang, profile, consent, openConsent, m365, toast } = useStudio();
  const [busy, setBusy] = useState("");
  
  if (teams) return <div className="stack" style={{ gap: 8, alignItems: "flex-start" }}><p className="faint" style={{ fontSize: 13 }}>{t(lang, "today.connectTeams")}</p><button className="btn sm" disabled={!!busy} onClick={() => void connect("teams")}>{busy === "teams" ? t(lang, "today.waiting") : (lang === "sq" ? "Lidh Teams" : T("Connect Teams"))}</button></div>;
  async function connect(kind: "google" | "outlook" | "teams") {
    if (!consent.m365) { openConsent(); return; }
    setBusy(kind);
    try {
      if (kind === "google") { await connectGoogle(); setMailProvider("google"); } else if (kind === "teams") await m365.connect("teams"); else { await m365.connect("outlook"); setMailProvider("outlook"); }
      toast(t(lang, "today.connected"));
    } catch (e) { toast(cleanError(e)); } finally { setBusy(""); }
  }
  const gmail = googleAvailable() && <button key="g" className="btn primary sm" disabled={!!busy} onClick={() => void connect("google")}>{busy === "google" ? t(lang, "today.waiting") : t(lang, "today.connectGmail")}</button>;
  const outlook = m365Available() && <button key="o" className="btn sm" disabled={!!busy} onClick={() => void connect("outlook")}>{busy === "outlook" ? t(lang, "today.waiting") : t(lang, "today.connectOutlook")}</button>;
  const buttons = profile.key === "client-edutask" ? [gmail, outlook] : [outlook, gmail];
  return <div className="stack" style={{ gap: 8, alignItems: "flex-start" }}><p className="faint" style={{ fontSize: 13 }}>{t(lang, "today.connect")}</p>
    <div className="row" style={{ gap: 6 }}>{buttons.some(Boolean) ? buttons : <button className="btn sm" onClick={() => go("connections")}>{t(lang, "today.openConnections")}</button>}</div></div>;
}
/** An error never leaves the person stuck: for email and calendar it also offers Gmail instead, or disconnecting Outlook. */
function Err({ msg, retry, mail }: { msg: string; retry: () => void; mail?: boolean }) {
  const { lang, m365, toast } = useStudio();
  const [busy, setBusy] = useState(false);
  
  const outlook = mail && !mailFromGoogle() && m365.connected("outlook");
  async function useGmail() {
    setBusy(true);
    try { await connectGoogle(); setMailProvider("google"); toast(t(lang, "today.connected")); } catch (e) { toast(cleanError(e)); } finally { setBusy(false); }
  }
  return <div className="stack" style={{ gap: 6, alignItems: "flex-start" }}><p style={{ color: "var(--bad)", fontSize: 12.5 }}>{msg}</p>
    <div className="row" style={{ gap: 6 }}>
      <button className="btn sm" onClick={retry}>{t(lang, "today.retry")}</button>
      {outlook && googleAvailable() && <button className="btn primary sm" disabled={busy} onClick={() => void useGmail()}>{busy ? t(lang, "today.waiting") : t(lang, "today.gmailInstead")}</button>}
      {outlook && <button className="btn sm" onClick={() => m365.disconnect("outlook")}>{t(lang, "today.disconnectOutlook")}</button>}
    </div></div>;
}
function Loading() { const { lang } = useStudio(); return <p className="faint" style={{ fontSize: 13 }}>{t(lang, "today.loading")}</p>; }

/** One event from a calendar range (calendar.view): Outlook and Google give the same fields. */
export type RangeEvent = { id: string; subject: string; start: string; end: string; allDay?: boolean; joinUrl: string | null; webLink: string; location: string };
export type CalView = "day" | "week" | "month";

const DATE_ONLY = /^\d{4}-\d{2}-\d{2}$/;

/** A local date as YYYY-MM-DD (what calendar.view takes, and the key events are grouped by). */
export const ymd = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

/** The dates the week view (today and the next six days) and the month view (this calendar month) ask for. */
export function calRange(view: "week" | "month", now = new Date()): { start: string; end: string; days: string[] } {
  const first = view === "week" ? new Date(now.getFullYear(), now.getMonth(), now.getDate()) : new Date(now.getFullYear(), now.getMonth(), 1);
  const count = view === "week" ? 7 : new Date(now.getFullYear(), now.getMonth() + 1, 0).getDate();
  const days = Array.from({ length: count }, (_, i) => ymd(new Date(first.getFullYear(), first.getMonth(), first.getDate() + i)));
  return { start: days[0]!, end: days[days.length - 1]!, days };
}

/** Events by the local day they start on (all-day events by their date), earliest first. */
export function byDay(events: RangeEvent[]): Record<string, RangeEvent[]> {
  const out: Record<string, RangeEvent[]> = {};
  for (const e of events) {
    const key = DATE_ONLY.test(e.start) ? e.start : ymd(new Date(e.start));
    (out[key] ??= []).push(e);
  }
  for (const k of Object.keys(out)) out[k]!.sort((a, b) => a.start.localeCompare(b.start));
  return out;
}

/** Monday first: how many empty cells come before the 1st in the month grid. */
export const leadingBlanks = (firstDay: string) => (new Date(`${firstDay}T12:00:00`).getDay() + 6) % 7;

function RangeEventRow({ e }: { e: RangeEvent }) {
  const { lang } = useStudio();
  return (
    <div className="today-item">
      <span className="t">{e.allDay || DATE_ONLY.test(e.start) ? T("All day") : timeOf(e.start)}</span> <b>{e.subject}</b>
      <div className="row" style={{ gap: 6, marginTop: 4 }}>
        {e.joinUrl && <button className="btn sm" onClick={() => openLink(e.joinUrl)}>{t(lang, "today.join")}</button>}
        {e.webLink && <button className="btn sm" onClick={() => openLink(e.webLink)}>{t(lang, "today.open")}</button>}
      </div>
    </div>
  );
}

/** The week and the month: the same calendar the Day view and VERA read, a glance only (booking stays in New meeting). */
/** The day `offset` weeks or months away from today (the calendar's ‹ and › buttons). */
export function shifted(view: "week" | "month", offset: number, now = new Date()): Date {
  return view === "week" ? new Date(now.getFullYear(), now.getMonth(), now.getDate() + offset * 7) : new Date(now.getFullYear(), now.getMonth() + offset, offset ? 1 : now.getDate());
}

function CalRange({ view, reloadKey }: { view: "week" | "month"; reloadKey: number }) {
  const { lang } = useStudio();
  const [offset, setOffset] = useState(0);
  const range = calRange(view, shifted(view, offset));
  const { data, error, loading, reload } = useGraph<{ events: RangeEvent[] }>("calendar.view", true, { start: range.start, end: range.end, k: reloadKey });
  const [picked, setPicked] = useState<string>(ymd(new Date()));
  useEffect(() => { setOffset(0); }, [view]);
  // Moving to another month picks its first day (today when back on this month).
  useEffect(() => { setPicked(offset ? range.start : ymd(new Date())); }, [offset, view]); // eslint-disable-line react-hooks/exhaustive-deps
  const label = (d: string, o: Intl.DateTimeFormatOptions) => new Date(`${d}T12:00:00`).toLocaleDateString(dateLocale(lang), o);
  const nav = (
    <div className="row between" style={{ gap: 4 }}>
      <button type="button" className="iconbtn ghost" onClick={() => setOffset((o) => o - 1)} aria-label={view === "week" ? T("Previous week") : T("Previous month")}>‹</button>
      <b style={{ fontSize: 13 }}>{view === "month" ? label(range.start, { month: "long", year: "numeric" }) : `${label(range.start, { day: "numeric", month: "short" })} – ${label(range.end, { day: "numeric", month: "short" })}`}</b>
      <div className="row" style={{ gap: 4 }}>
        {offset !== 0 && <button type="button" className="btn sm" onClick={() => setOffset(0)}>{T("Today")}</button>}
        <button type="button" className="iconbtn ghost" onClick={() => setOffset((o) => o + 1)} aria-label={view === "week" ? T("Next week") : T("Next month")}>›</button>
      </div>
    </div>);
  if (loading && !data) return <>{nav}<Loading /></>;
  if (error) return <>{nav}<Err mail msg={error} retry={() => void reload()} /></>;
  const days = byDay(data?.events ?? []);
  if (view === "week") {
    const busy = range.days.filter((d) => days[d]?.length);
    if (!busy.length) return <>{nav}<p className="faint" style={{ fontSize: 13 }}>{T("Nothing in your calendar this week.")}</p></>;
    return <>{nav}{busy.map((d) => (
      <div key={d} className="stack" style={{ gap: 4 }}>
        <span className="label">{label(d, { weekday: "short", day: "numeric", month: "short" })}</span>
        {days[d]!.map((e) => <RangeEventRow key={e.id} e={e} />)}
      </div>))}</>;
  }
  const today = ymd(new Date());
  const weekdays = Array.from({ length: 7 }, (_, i) => label(ymd(new Date(2026, 10, 2 + i)), { weekday: "narrow" }));   // 2 Nov 2026 is a Monday
  return <>
    {nav}
    <div className="cal-month" aria-label={label(range.start, { month: "long", year: "numeric" })} style={{ display: "grid", gridTemplateColumns: "repeat(7, minmax(0, 1fr))", gap: 2, textAlign: "center", fontSize: 12 }}>
      {weekdays.map((w, i) => <span key={`w${i}`} className="faint" aria-hidden="true">{w}</span>)}
      {Array.from({ length: leadingBlanks(range.start) }, (_, i) => <span key={`b${i}`} />)}
      {range.days.map((d) => {
        const n = days[d]?.length ?? 0;
        const chosen = d === picked;
        return (
          <button key={d} type="button" className="btn sm" aria-pressed={chosen} aria-label={`${label(d, { weekday: "long", day: "numeric", month: "long" })}${n ? `, ${n}` : ""}`}
            onClick={() => setPicked(d)}
            style={{ padding: "4px 0", minHeight: 34, fontWeight: d === today ? 700 : 400, background: chosen ? "#6D3FD6" : undefined, color: chosen ? "#fff" : undefined }}>
            {Number(d.slice(8))}
            <span aria-hidden="true" className="cal-dot" style={{ display: "block", height: 6, lineHeight: "6px" }}>{n ? "•" : ""}</span>
            {/* On a wide month (Home, the whole row) each day shows its first two events by name. */}
            {days[d]?.slice(0, 2).map((e) => <span key={e.id} className="cal-ev" aria-hidden="true">{e.subject}</span>)}
            {n > 2 && <span className="cal-ev faint" aria-hidden="true">+{n - 2}</span>}
          </button>);
      })}
    </div>
    <span className="label">{label(picked, { weekday: "long", day: "numeric", month: "long" })}</span>
    {days[picked]?.length ? days[picked]!.map((e) => <RangeEventRow key={e.id} e={e} />) : <p className="faint" style={{ fontSize: 13 }}>{T("Nothing in your calendar that day.")}</p>}
  </>;
}

export function TodayCal() {
  const { person, m365, lang, user } = useStudio();
  const on = m365.mail();
  const google = mailFromGoogle();
  // The view she picked stays open (per person, on this device) until she picks another.
  const [view, setViewState] = useState<CalView>(() => { try { const v = localStorage.getItem(`vera-cal-view-${user.id}`); return v === "week" || v === "month" ? v : "day"; } catch { return "day"; } });
  const setView = (v: CalView) => { setViewState(v); try { localStorage.setItem(`vera-cal-view-${user.id}`, v); } catch { /* this visit only */ } };
  const [reloadKey, setReloadKey] = useState(0);
  const { data, error, loading, reload } = useGraph<CalEvent[]>("calendar.day", on && view === "day", { days: 1 });
  const [meet, setMeet] = useState(false);
  if (devMode) {
    const rows = person?.today?.cal ?? [];
    return <>{rows.map(([tm, a, b]) => <div className="today-item" key={tm + a}><span className="t">{tm}</span> <b>{a}</b><div className="faint" style={{ fontSize: 12 }}>{b}</div></div>)}</>;
  }
  if (!on) return <ConnectHint />;
  const views: [CalView, string][] = [["day", T("Day")], ["week", T("Week")], ["month", T("Month")]];
  return <>
    <div className="row" role="group" aria-label={T("Calendar view")} data-cal={view} style={{ gap: 4 }}>
      {views.map(([v, name]) => <button key={v} type="button" className="btn sm" aria-pressed={view === v} onClick={() => setView(v)} style={view === v ? { fontWeight: 700 } : undefined}>{name}</button>)}
    </div>
    {view !== "day" ? <CalRange view={view} reloadKey={reloadKey} />
      : loading && !data ? <Loading /> : error ? <Err mail msg={error} retry={() => void reload()} /> : !data?.length ? <p className="faint" style={{ fontSize: 13 }}>{t(lang, "today.calEmpty")}</p>
      : data.map((e) => (
        <div className="today-item" key={e.id}>
          <span className="t">{timeOf(e.start)}</span> <b>{e.subject}</b>
          <div className="faint" style={{ fontSize: 12 }}>{[e.teams ? (google ? "Google Meet" : "Teams") : e.location, e.attendees.slice(0, 3).join(", ")].filter(Boolean).join(" · ")}</div>
          <div className="row" style={{ gap: 6, marginTop: 6 }}>
            {e.joinUrl && <button className="btn sm"  onClick={() => openLink(e.joinUrl)}>{t(lang, "today.join")}</button>}
            <button className="btn sm"  onClick={() => openLink(e.webLink)}>{t(lang, "today.open")}</button>
          </div>
        </div>))}
    <button className="btn sm" onClick={() => setMeet(true)}><Icon name={google ? "cal" : "teams"} size={15} /> {t(lang, "today.newMeeting", { app: google ? "Google Meet" : "Teams" })}</button>
    {meet && <MeetingDialog onClose={() => { setMeet(false); void reload(); setReloadKey((k) => k + 1); }} />}
  </>;
}

export function TodayMail({ onDraft }: { onDraft?: (from: string) => void }) {
  const { person, go, m365, toast, lang } = useStudio();
  const on = m365.mail();
  const google = mailFromGoogle();
  const { data, error, loading, reload } = useGraph<Mail[]>("mail.inbox", on);
  if (devMode) {
    const rows = person?.today?.mail ?? [];
    return <>{rows.map(([f, s]) => (
      <div className="today-item" key={f + s}><b>{f}</b><div className="mute" style={{ fontSize: 12.5 }}>{s}</div>
        <button className="btn sm" style={{ marginTop: 8 }} onClick={() => (onDraft ? onDraft(f) : go("workspace", { ask: lang === "sq" ? `Harto një përgjigje për ${f} rreth "${s}"` : `Draft a reply to ${f} about "${s}"` }))}>{t(lang, "today.draftReply")}</button>
      </div>))}</>;
  }
  if (!on) return <ConnectHint />;
  async function ask(m: Mail) {
    try {
      const full = await graph<{ subject: string; text: string; from: { name: string } }>("mail.read", { id: m.id });
      go("workspace", {
        ask: lang === "sq" ? `Më ndihmo t'i përgjigjem ${full.from.name || "këtij emaili"}` : `Help me reply to ${full.from.name || "this email"}`,
        files: [{ name: `Email: ${full.subject || (lang === "sq" ? "(pa subjekt)" : "(no subject)")}`, text: `From: ${full.from.name}\nSubject: ${full.subject}\n\n${full.text}` }], fresh: true, title: `Reply: ${full.subject}`.slice(0, 60),
      });
    } catch (e) { toast(e instanceof Error ? e.message : lang === "sq" ? "Ky email nuk u hap." : T("Couldn't open that email.")); }
  }
  return <>
    {loading && !data ? <Loading /> : error ? <Err mail msg={error} retry={() => void reload()} /> : !data?.length ? <p className="faint" style={{ fontSize: 13 }}>{t(lang, "today.inboxEmpty")}</p>
      : data.slice(0, 6).map((m) => (
        <div className="today-item" key={m.id}>
          <div className="row" style={{ justifyContent: "space-between", flexWrap: "nowrap" }}><b style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{m.unread && <span className="unread" />}{m.from.name || m.from.email}</b><span className="faint" style={{ fontSize: 11.5, flex: "none" }}>{timeOf(m.received)}</span></div>
          <div className="mute" style={{ fontSize: 12.5 }}>{m.subject}</div>
          <div className="row" style={{ gap: 6, marginTop: 8 }}>
            <button className="btn sm"  onClick={() => void ask(m)}>{t(lang, "today.ask")}</button>
            <button className="btn sm"  onClick={() => openLink(m.webLink)}>{t(lang, "today.openIn", { app: google ? "Gmail" : "Outlook" })}</button>
          </div>
        </div>))}
  </>;
}

export function TodayTeams() {
  const { person, m365, lang } = useStudio();
  const on = m365.connected("teams");
  const { data, error, loading, reload } = useGraph<Chat[]>("teams.chats", on);
  const openTeams = () => openLink("https://teams.microsoft.com/");
  if (devMode) {
    const rows = person?.today?.teams ?? [];
    return <>{rows.map(([c, m]) => <div className="today-item" key={c + m}><span className="t">{c}</span><div className="mute" style={{ fontSize: 12.5 }}>{m}</div></div>)}<button className="btn sm" onClick={openTeams}>{t(lang, "today.openTeams")}</button></>;
  }
  if (!on) return <ConnectHint teams />;
  return <>
    {loading && !data ? <Loading /> : error ? <Err msg={error} retry={() => void reload()} /> : !data?.length ? <p className="faint" style={{ fontSize: 13 }}>{t(lang, "today.noChats")}</p>
      : data.slice(0, 5).map((c) => (
        <button className="today-item linkish" key={c.id} onClick={() => openLink(c.webUrl)}>
          <span className="t">{c.topic || c.last?.from || t(lang, c.type === "oneOnOne" ? "today.chat" : "today.groupChat")}</span>
          <div className="mute" style={{ fontSize: 12.5 }}>{c.last ? `${c.last.from ? `${c.last.from}: ` : ""}${c.last.text}` : t(lang, "today.noMessages")}</div>
        </button>))}
    <button className="btn sm" onClick={openTeams}>{t(lang, "today.openTeams")}</button>
  </>;
}

/** Upcoming proposal deadlines (for companies with Proposals, such as EduTask). */
export function TodayDeadlines() {
  const { go, lang } = useStudio();
  const [list, setList] = useState<Proposal[] | null>(null);
  useEffect(() => { listProposals().then(setList).catch(() => setList([])); }, []);
  if (list === null) return <Loading />;
  const today = new Date(new Date().toDateString()).getTime();
  const soon = list.filter((p) => p.deadline && !["submitted", "awarded", "rejected"].includes(p.stage) && new Date(`${p.deadline}T00:00:00`).getTime() >= today)
    .sort((a, b) => (a.deadline ?? "").localeCompare(b.deadline ?? "")).slice(0, 5);
  const days = (d: string) => Math.round((new Date(`${d}T00:00:00`).getTime() - today) / 86_400_000);
  return <>
    {!soon.length ? <p className="faint" style={{ fontSize: 13 }}>{t(lang, "today.noDeadlines")}</p>
      : soon.map((p) => (
        <button className="today-item linkish" key={p.id} onClick={() => go("proposals")}>
          <span className={`t ${days(p.deadline!) <= 7 ? "due-soon" : ""}`}>{p.deadline} · {lang === "sq" ? (days(p.deadline!) === 0 ? "sot" : `pas ${days(p.deadline!)} ditësh`) : (days(p.deadline!) === 0 ? "today" : `in ${days(p.deadline!)} days`)}</span>
          <div className="mute" style={{ fontSize: 12.5 }}>{p.title}{p.client ? ` · ${p.client}` : ""}</div>
        </button>))}
    <button className="btn sm" onClick={() => go("proposals")}>{t(lang, "today.allProposals")}</button>
  </>;
}
